import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Star, CheckCircle, Phone, Mail, MapPin, ArrowRight, Shield, Users, FileCheck, Menu, X, ShieldCheck, TrendingUp, CircleDollarSign, Award, MessageSquare, ChevronDown, FileText, GraduationCap, Search } from 'lucide-react';
import ServiceCard from './components/ServiceCard'; // Import the new component
import ServicesCarousel from './components/ServicesCarousel'; // Will be created next
import ConfirmationModal from './components/ConfirmationModal'; // RE-IMPORT the modal
import ConsultationFormModal from './components/ConsultationFormModal'; // Import the new form modal
import HeroCarousel from './components/HeroCarousel'; // Import the new Hero Carousel

// Helper function (like a custom hook) for Intersection Observer logic
function useElementVisibility(ref: React.RefObject<HTMLElement>, options: IntersectionObserverInit = { threshold: 0.1 }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(([entry]) => {
      // Trigger only once when it becomes visible enough
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(element); // Stop observing once visible
      }
    }, options);

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, options.threshold, options.root, options.rootMargin]); // Include options in deps

  return isVisible;
}

function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const logoRef = useRef<HTMLImageElement>(null);
  const [backgroundWidth, setBackgroundWidth] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false); // RE-ADD State for modal visibility
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false); // State for form modal
  const [showWhatsAppBubble, setShowWhatsAppBubble] = useState(false); // ADD State for WhatsApp bubble visibility
  const [hasBubbleAutoAppeared, setHasBubbleAutoAppeared] = useState(false); // ADD state to track if bubble auto-appeared once
  const [bubbleAnimationClass, setBubbleAnimationClass] = useState(''); // ADD state for animation class
  const [isCourseDropdownOpen, setIsCourseDropdownOpen] = useState(false); // ADD State for course dropdown
  const [isDocumentDropdownOpen, setIsDocumentDropdownOpen] = useState(false); // ADD State for document dropdown
  const [courseSearchFilter, setCourseSearchFilter] = useState(''); // ADD State for course search filter
  
  // Refs for sections and elements
  const servicesSectionRef = useRef<HTMLElement>(null);
  const servicesGridRef = useRef<HTMLDivElement>(null);
  const whySalusSectionRef = useRef<HTMLElement>(null);
  const carouselSectionRef = useRef<HTMLElement>(null); // New ref for carousel
  const ctaSectionRef = useRef<HTMLElement>(null);
  const contactSectionRef = useRef<HTMLElement>(null);
  const contactGridRef = useRef<HTMLDivElement>(null);
  const whatsAppButtonRef = useRef<HTMLAnchorElement>(null); // ADD Ref for WhatsApp button
  const hideBubbleTimeoutRef = useRef<NodeJS.Timeout | null>(null); // ADD ref for hide timeout
  const documentsSectionRef = useRef<HTMLElement>(null); // ADD Ref for new documents section
  
  // Visibility states from observer
  const isServicesVisible = useElementVisibility(servicesSectionRef);
  const isServicesGridVisible = useElementVisibility(servicesGridRef, { threshold: 0.05 });
  const isWhySalusVisible = useElementVisibility(whySalusSectionRef);
  const isCarouselVisible = useElementVisibility(carouselSectionRef); // New state for carousel animation
  const isCtaVisible = useElementVisibility(ctaSectionRef);
  const isContactVisible = useElementVisibility(contactSectionRef);
  const isContactGridVisible = useElementVisibility(contactGridRef, { threshold: 0.05 });
  const isWhatsAppButtonVisible = useElementVisibility(whatsAppButtonRef); // ADD Visibility check for WhatsApp button
  const isDocumentsSectionVisible = useElementVisibility(documentsSectionRef); // ADD Visibility check for new section

  useEffect(() => {
    // Ensure browser doesn't restore scroll position automatically
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []); // Remove popupClosedManually dependency

  useLayoutEffect(() => {
    const updateWidth = () => {
      if (logoRef.current) {
        const logoRightEdge = logoRef.current.getBoundingClientRect().right;
        const padding = 16;
        setBackgroundWidth(logoRightEdge + padding);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);

    const logoImg = logoRef.current;
    if (logoImg) {
      logoImg.addEventListener('load', updateWidth);
    }

    return () => {
      window.removeEventListener('resize', updateWidth);
      if (logoImg) {
        logoImg.removeEventListener('load', updateWidth);
      }
    };
  }, []);

  // ADD useEffect for WhatsApp Bubble Timer
  useEffect(() => {
    let timerId: NodeJS.Timeout | null = null;

    // Start timer only if button is visible AND bubble hasn't auto-appeared yet
    if (isWhatsAppButtonVisible && !hasBubbleAutoAppeared) { 
      timerId = setTimeout(() => {
         // Only show if hasn't auto-appeared yet when timer fires
        if (!hasBubbleAutoAppeared) {
             setShowWhatsAppBubble(true);
             setBubbleAnimationClass('animate-slide-in-bubble'); // Set entry animation
             setHasBubbleAutoAppeared(true); // Mark as auto-appeared
        }
      }, 2000); // 2 seconds delay
    }

    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  // Depend on visibility and the auto-appeared flag
  }, [isWhatsAppButtonVisible, hasBubbleAutoAppeared]);

  // Define detail item type structure
  type ServiceDetailItem = { 
      text: string; 
      isSubItem?: boolean; 
      isHeader?: boolean; 
  };

  // Define service type including the new detail structure
  type Service = {
    icon: React.ElementType;
    title: string;
    description: string;
    details?: ServiceDetailItem[]; // Use the new type here
  };

  const services: Service[] = [ // Add type annotation
    {
      icon: Shield,
      title: "Consultoria em Segurança e Saúde no Trabalho",
      description: "Orientação especializada e soluções para garantir que sua empresa atenda a todas as normas regulamentadoras e proporcione um ambiente de trabalho seguro e saudável.",
      details: [
        { text: "Análise de riscos ocupacionais" }, 
        { text: "Análise de documentação já emitida" },
        { text: "Visita Técnica de Auditoria" },
        { text: "Medições de:", isHeader: true }, // Mark as header
        { text: "Ruído", isSubItem: true }, // Mark as subitem
        { text: "Temperatura", isSubItem: true }, // Mark as subitem
        { text: "Vibração", isSubItem: true } // Mark as subitem
      ]
    },
    {
      icon: FileCheck,
      title: "Elaboração de Documentos de Saúde e Segurança do Trabalho",
      description: "Desenvolvimento e atualização de todos os documentos essenciais de segurança e saúde do trabalho, garantindo conformidade legal e reduzindo riscos.",
      details: [
        // Convert existing simple strings to the new structure
        { text: "PGR - Programa de Gerenciamento de Riscos" }, 
        { text: "PCMSO - Programa de Controle Médico de Saúde Ocupacional" },
        { text: "LTCAT - Laudo Técnico das Condições Ambientais do Trabalho" }, 
        { text: "PPP - Perfil Profissiográfico Previdenciário" } 
      ]
    },
    {
      icon: Users,
      title: "Treinamentos em Saúde e Segurança do Trabalho (EAD e Presenciais)",
      description: "Capacitação especializada nas normas regulamentadoras e boas práticas de segurança, disponíveis tanto em formato online quanto presencial.",
      details: [
        // Convert existing simple strings to the new structure
        { text: "NR-10, NR-12, NR-35 e outras normas" },
        { text: "CIPA - Comissão Interna de Prevenção de Acidentes" },
        { text: "Primeiros socorros e brigada de incêndio" }
      ]
    }
  ];

  const benefits = [
    {
      icon: ShieldCheck,
      title: "Proteção legal e conformidade",
      description: "Conformidade com a legislação trabalhista e prevenção de penalidades legais, multas e processos trabalhistas."
    },
    {
      icon: TrendingUp,
      title: "Aumento da produtividade",
      description: "Redução do absenteísmo, melhoria do clima organizacional e aumento da eficiência operacional."
    },
    {
      icon: CircleDollarSign,
      title: "Redução de custos",
      description: "Diminuição de gastos com acidentes, doenças ocupacionais, seguros e afastamentos."
    },
    {
      icon: Award,
      title: "Melhoria da imagem corporativa",
      description: "Fortalecimento da reputação da empresa como responsável e comprometida com o bem-estar dos colaboradores."
    }
  ];

  // Array with offered documents
  const offeredDocuments = [
    { title: "PGR - Programa de Gerenciamento de Riscos" },
    { title: "PCMSO - Programa de Controle Médico de Saúde Ocupacional" },
    { title: "LTCAT - Laudo Técnico das Condições Ambientais do Trabalho" },
    { title: "PPP - Perfil Profissiográfico Previdenciário" },
    { title: "AET - Análise Ergonômica do Trabalho" },
    { title: "AEP - Análise Ergonômica Preliminar" },
    { title: "CAT - Comunicado de Acidente do Trabalho" },
    { title: "LTIP - Laudo Técnico de Insalubridade e Periculosidade" }
  ];

  // Função para ordenar cursos: NR primeiro (alfanumérico), depois outros (alfabético)
  const sortCourses = (courses: any[]) => {
    return courses.sort((a, b) => {
      const titleA = a.title.toUpperCase();
      const titleB = b.title.toUpperCase();
      
      const isNRA = titleA.startsWith('NR ');
      const isNRB = titleB.startsWith('NR ');
      
      // Se ambos são NR ou ambos não são NR, ordena alfabeticamente
      if (isNRA === isNRB) {
        return titleA.localeCompare(titleB, 'pt-BR', { numeric: true });
      }
      
      // NR vem primeiro
      return isNRA ? -1 : 1;
    });
  };

  // Dados dos cursos baseados no arquivo Cursos.md - TODOS OS 47 CURSOS
  const carouselItemsUnsorted = [
    {
      image: `/images/courses/apr_-_analise_preliminar_de_risco.webp`,
      title: "APR - Análise Preliminar de Risco",
      description: "Carga Horária: 1 hora",
      link: "https://ead.salusocupacional.com.br/curso/apr-analise-preliminar-de-risco/"
    },
    {
      image: `/images/courses/combate_a_incendio.webp`,
      title: "Combate a Incêndio",
      description: "Carga Horária: 8 horas",
      link: "https://ead.salusocupacional.com.br/curso/combate-a-incendio-08-horas/"
    },
    {
      image: `/images/courses/conhecendo_as_normas_regulamentadoras_do_tem.webp`,
      title: "Conhecendo as Normas Regulamentadoras do TEM",
      description: "Carga Horária: 2 horas",
      link: "https://ead.salusocupacional.com.br/curso/conhecendo-as-normas-regulamentadoras-do-mte/"
    },
    {
      image: `/images/courses/lei_lucas_n_13.722_-_primeiros_socorros.webp`,
      title: "Lei Lucas nº 13.722 - Primeiros Socorros",
      description: "Carga Horária: 10 horas",
      link: "https://ead.salusocupacional.com.br/curso/lei-lucas-no-13-722-primeiros-socorros-10-horas/"
    },
    {
      image: `/images/courses/loto_-_lockout_e_tagout.webp`,
      title: "LOTO - Lockout e Tagout",
      description: "Carga Horária: 2 horas",
      link: "https://ead.salusocupacional.com.br/curso/loto-lockout-e-tagout/"
    },
    {
      image: `/images/courses/nr_01_-_treinamento_de_integracao_de_seguranca_2.webp`,
      title: "NR 01 - Treinamento De Integração De Segurança (2 Horas)",
      description: "Carga Horária: 2 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-1-treinamento-de-integracao-de-seguranca-02-horas/"
    },
    {
      image: `/images/courses/nr_01_-_treinamento_de_integracao_de_seguranca_4.webp`,
      title: "NR 01 - Treinamento De Integração De Segurança (6 Horas)",
      description: "Carga Horária: 6 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-1-treinamento-de-integracao-de-seguranca-06-horas/"
    },
    {
      image: `/images/courses/nr_05_cipa_-comissao_interna_de_prevencao_a_acidentes-grau_de_risco_2-_parte_teorica.webp`,
      title: "NR 05 CIPA - Comissão Interna De Prevenção A Acidentes - Grau De Risco 2 - Parte Teórica",
      description: "Carga Horária: 8 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-05-cipa-comissao-interna-de-prevencao-de-acidentes-e-assedio-grau-de-risco-2/"
    },
    {
      image: `/images/courses/nr_05_cipa_-comissao_interna_de_prevencao_a_acidentes-grau_de_risco_3-_parte_teorica.webp`,
      title: "NR 05 CIPA - Comissão Interna De Prevenção A Acidentes - Grau De Risco 3 - Parte Teórica",
      description: "Carga Horária: 8 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-05-cipa-comissao-interna-de-prevencao-de-acidentes-e-assedio-grau-de-risco-3/"
    },
    {
      image: `/images/courses/nr_05_cipa_-comissao_interna_de_prevencao_de_acidentes_e_assedio-_grau_de_risco_1.webp`,
      title: "NR 05 CIPA - Comissão Interna De Prevenção De Acidentes E Assédio - Grau De Risco 1",
      description: "Carga Horária: 8 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-05-cipa-comissao-interna-de-prevencao-de-acidentes-e-assedio-grau-de-risco-1/"
    },
    {
      image: `/images/courses/nr_05_cipa_-comissao_interna_de_prevencao_de_acidentes_e_assedio-representante_nomeado-_grau_de_risco_1.webp`,
      title: "NR 05 CIPA - Comissão Interna de Prevenção de Acidentes e Assédio - Representante Nomeado - Grau De Risco 1",
      description: "Carga Horária: 8 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-05-cipa-nomeado-comissao-interna-de-prevencao-de-acidentes-e-assedio-grau-de-risco-1/"
    },
    {
      image: `/images/courses/nr_05_cipa_-comissao_interna_de_prevencao_de_acidentes_e_assedio-representante_nomeado-_grau_de_risco_2.webp`,
      title: "NR 05 CIPA - Comissão Interna de Prevenção de Acidentes e Assédio - Representante Nomeado - Grau De Risco 2",
      description: "Carga Horária: 12 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-05-cipa-nomeado-comissao-interna-de-prevencao-de-acidentes-e-assedio-grau-de-risco-2/"
    },
    {
      image: `/images/courses/nr_05_cipa_-comissao_interna_de_prevencao_de_acidentes_e_assedio-representante_nomeado-_grau_de_risco_4.webp`,
      title: "NR 05 CIPA - Comissão Interna de Prevenção de Acidentes e Assédio - Representante Nomeado - Grau De Risco 4",
      description: "Carga Horária: 20 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-05-cipa-nomeado-comissao-interna-de-prevencao-a-acidentes-grau-de-risco-4/"
    },
    {
      image: `/images/courses/nr_05_cipa_-comissao_interna_de_prevencao_de_acidentes_e_de_assedio-grau_de_risco_4-_parte_teorica.webp`,
      title: "NR 05 Cipa - Comissão Interna De Prevenção De Acidentes E De Assédio - Grau De Risco 4 - Parte Teórica",
      description: "Carga Horária: 12 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-05-cipa-comissao-interna-de-prevencao-de-acidentes-e-assedio-grau-de-risco-4/"
    },
    {
      image: `/images/courses/nr_05_cipa_nomeado_-comissao_interna_de_prevencao_a_acidentes-_grau_de_risco_3.webp`,
      title: "NR 05 CIPA Nomeado - Comissão Interna de Prevenção a Acidentes - Grau de Risco 3",
      description: "Carga Horária: 16 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-05-cipa-nomeado-comissao-interna-de-prevencao-a-acidentes-grau-de-risco-3/"
    },
    {
      image: `/images/courses/nr_06_-epi-_equipamento_de_protecao_individual.webp`,
      title: "NR 06 - EPI - Equipamento de Proteção Individual",
      description: "Carga Horária: 1 hora",
      link: "https://ead.salusocupacional.com.br/curso/nr-06-epi-equipamento-de-protecao-individual-01-hora/"
    },
    {
      image: `/images/courses/nr_10_-curso_complementar-_seguranca_no_sistema_eletrico_de_potencia_sep_e_em_suas_proximidades.webp`,
      title: "NR 10 - Curso Complementar - Segurança No Sistema Elétrico De Potência (SEP) E Em Suas Proximidades",
      description: "Carga Horária: 40 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-10-curso-complementar-seguranca-no-sistema-eletrico-de-potencia-sep-e-em-suas-proximidades-2/"
    },
    {
      image: `/images/courses/nr_10_-seguranca_em_servico_com_eletricidade-_basico.webp`,
      title: "NR 10 - Segurança Em Serviço Com Eletricidade - Básico",
      description: "Carga Horária: 40 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-10-seguranca-em-servico-com-eletricidade-basico/"
    },
    {
      image: `/images/courses/nr_11_-_operador_maquinas_agricolas_e_equipamentos.webp`,
      title: "NR 11 - Operador Máquinas Agrícolas e Equipamentos",
      description: "Carga Horária: 16 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-11-operador-maquinas-agricolas-e-equipamentos/"
    },
    {
      image: `/images/courses/nr_11_-operacao_de_ponte_rolante_e_talha_eletrica-_teorico.webp`,
      title: "NR 11 - Operação de Ponte Rolante e Talha elétrica - Teórico",
      description: "Carga Horária: 16 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-11-operacao-de-ponte-rolante-e-talha-eletrica-teorico/"
    },
    {
      image: `/images/courses/nr_12_-maquinas_e_equipamentos-_ferramentas_eletricas_e_manuais.webp`,
      title: "NR 12 - Máquinas e Equipamentos - Ferramentas Elétricas e Manuais",
      description: "Carga Horária: 4 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-12-maquinas-e-equipamentos-ferramentas-eletricas-e-manuais/"
    },
    {
      image: `/images/courses/nr_12_-maquinas_e_equipamentos-_maquinas_para_acougue_mercearia_bares_e_restaurantes.webp`,
      title: "NR 12 - Máquinas e Equipamentos - Máquinas para Açougue, Mercearia, Bares e Restaurantes",
      description: "Carga Horária: 4 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-12-maquinas-e-equipamentos-maquinas-para-acougue-mercearia-bares-e-restaurantes/"
    },
    {
      image: `/images/courses/nr_12_-maquinas_e_equipamentos-_maquinas_para_panificacao_e_confeitaria.webp`,
      title: "NR 12 - Máquinas e Equipamentos - Máquinas para Panificação e Confeitaria",
      description: "Carga Horária: 4 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-12-maquinas-e-equipamentos-maquinas-para-panificacao-e-confeitaria/"
    },
    {
      image: `/images/courses/nr_12_-maquinas_e_equipamentos-_prensas_e_similares.webp`,
      title: "NR 12 - Máquinas e Equipamentos - Prensas e Similares",
      description: "Carga Horária: 4 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-12-maquinas-e-equipamentos-prensas-e-similares/"
    },
    {
      image: `/images/courses/nr_12_-_maquinas_e_equipamentos.webp`,
      title: "NR 12 - Máquinas e Equipamentos",
      description: "Carga Horária: 4 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-12-maquinas-e-equipamentos-4-horas/"
    },
    {
      image: `/images/courses/nr_13_-_treinamento_de_seguranca_na_operacao_de_caldeiras.webp`,
      title: "NR 13 - Treinamento de Segurança na Operação de Caldeiras",
      description: "Carga Horária: 40 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-13-treinamento-de-seguranca-na-operacao-de-caldeiras-2/"
    },
    {
      image: `/images/courses/nr_17_-_ergonomia.webp`,
      title: "NR 17 - Ergonomia",
      description: "Carga Horária: 4 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-17-ergonomia-2/"
    },
    {
      image: `/images/courses/nr_17_-_ergonomia_para_operadores_de_check-out.webp`,
      title: "NR 17 - Ergonomia para Operadores de Check-Out",
      description: "Carga Horária: 2 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-17-ergonomia-para-operadores-de-check-out/"
    },
    {
      image: `/images/courses/NR-17-–-Teleatendimento_Telemarketing-FORMASEG-3.webp`,
      title: "NR 17 - Ergonomia para Trabalho em Teleatendimento/Telemarketing",
      description: "Carga Horária: 4 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-17-ergonomia-para-trabalho-em-teleatendimento-telemarketing/"
    },
    {
      image: `/images/courses/nr_17_-_ginastica_laboral.webp`,
      title: "NR 17 - Ginástica Laboral",
      description: "Carga Horária: 1 hora",
      link: "https://ead.salusocupacional.com.br/curso/nr-17-ginastica-laboral/"
    },
    {
      image: `/images/courses/nr_18_-_integracao_de_seguranca_na_construcao_civil.webp`,
      title: "NR 18 - Integração de Segurança na Construção Civil",
      description: "Carga Horária: 4 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-18-integracao-de-seguranca-na-construcao-civil-4-horas/"
    },
    {
      image: `/images/courses/nr_20_-exposicao_ocupacional_ao_benzeno_em_postos_revendedores_de_combustiveis-_prc.webp`,
      title: "NR 20 - Exposição Ocupacional ao Benzeno em Postos Revendedores de Combustíveis - PRC",
      description: "Carga Horária: 4 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-20-exposicao-ocupacional-ao-benzeno-em-postos-revendedores-de-combustiveis-prc/"
    },
    {
      image: `/images/courses/nr_20_-_iniciacao_sobre_inflamaveis_e_combustiveis.webp`,
      title: "NR 20 - Iniciação Sobre Inflamáveis e Combustíveis",
      description: "Carga Horária: 3 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-20-iniciacao-sobre-inflamaveis-e-combustiveis/"
    },
    {
      image: `/images/courses/nr_20_-seguranca_com_liquidos_e_inflamaveis-_avancado_i.webp`,
      title: "NR 20 - Segurança com Líquidos e Inflamáveis - Avançado I",
      description: "Carga Horária: 20 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-20-seguranca-com-liquidos-e-inflamaveis-avancado-i/"
    },
    {
      image: `/images/courses/nr_20_-seguranca_com_liquidos_e_inflamaveis-especifico-_classe_ii.webp`,
      title: "NR 20 - Segurança com Líquidos e Inflamáveis - Específico - Classe II",
      description: "Carga Horária: 14 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-20-seguranca-com-liquidos-e-inflamaveis-especifico-classe-ii/"
    },
    {
      image: `/images/courses/nr_20_-seguranca_com_liquidos_e_inflamaveis-intermediario-_classe_ii.webp`,
      title: "NR 20 - Segurança com líquidos e Inflamáveis - Intermediário - Classe II",
      description: "Carga Horária: 14 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-20-seguranca-com-liquidos-e-inflamaveis-intermediario-classe-ii/"
    },
    {
      image: `/images/courses/nr_20_-seguranca_com_liquidos_e_inflamaveis-_intermediario_classe_i.webp`,
      title: "NR 20 - Segurança com líquidos e Inflamáveis - Intermediário Classe I",
      description: "Carga Horária: 12 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-20-seguranca-com-liquidos-e-inflamaveis-intermediario-classe-i/"
    },
    {
      image: `/images/courses/nr_20_-seguranca_com_liquidos_e_inflamaveis-_intermediario_classe_iii.webp`,
      title: "NR 20 - Segurança com líquidos e Inflamáveis - Intermediário Classe III",
      description: "Carga Horária: 16 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-20-seguranca-com-liquidos-e-inflamaveis-intermediario-classe-iii/"
    },
    {
      image: `/images/courses/nr_20_seguranca_com_liquidos_e_inflamaveis_-_avancado_ii.webp`,
      title: "NR 20 Segurança com Líquidos e Inflamáveis - Avançado II",
      description: "Carga Horária: 32 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-20-seguranca-com-liquidos-e-inflamaveis-avancado-ii/"
    },
    {
      image: `/images/courses/nr_20_seguranca_com_liquidos_e_inflamaveis_-especifico-_classe_iii.webp`,
      title: "NR 20 Segurança com Líquidos e Inflamáveis - Específico - Classe III",
      description: "Carga Horária: 16 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-20-seguranca-com-liquidos-e-inflamaveis-especifico-classe-iii/"
    },
    {
      image: `/images/courses/nr_26_-_sinalizacao_de_seguranca.webp`,
      title: "NR 26 - Sinalização de Segurança",
      description: "Carga Horária: 2 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-26-sinalizacao-de-seguranca/"
    },
    {
      image: `/images/courses/nr_32_-_treinamento_de_capacitacao_para_profissional_de_saude.webp`,
      title: "NR 32 - Treinamento de Capacitação para Profissional de Saúde",
      description: "Carga Horária: 10 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-32-treinamento-de-capacitacao-para-profissional-de-saude/"
    },
    {
      image: `/images/courses/nr_34.5_-seguranca_para_trabalhos_a_quente-_basico.webp`,
      title: "NR 34.5 - Segurança para Trabalhos a Quente - Básico",
      description: "Carga Horária: 8 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-34-5-seguranca-para-trabalhos-a-quente-basico/"
    },
    {
      image: `/images/courses/nr_35_-_trabalho_em_altura.webp`,
      title: "NR 35 - Trabalho em Altura",
      description: "Carga Horária: 8 horas",
      link: "http://ead.salusocupacional.com.br/curso/nr-35-trabalho-em-altura/"
    },
    {
      image: `/images/courses/nr_36_-_seguranca_e_saude_no_trabalho_em_empresas_de_abate_processamento_de_carnes_e_derivados.webp`,
      title: "NR 36 - Segurança e Saúde no Trabalho em Empresas de Abate, Processamento De Carnes e Derivados",
      description: "Carga Horária: 5 horas",
      link: "https://ead.salusocupacional.com.br/curso/nr-36-seguranca-e-saude-no-trabalho-em-empresas-de-abate-processamento-de-carnes-e-derivados/"
    },
    {
      image: `/images/courses/operacoes_de_soldagem_e_corte_a_quente.webp`,
      title: "Operações de Soldagem e Corte a Quente",
      description: "Carga Horária: 4 horas",
      link: "https://ead.salusocupacional.com.br/curso/operacoes-de-soldagem-e-corte-a-quente/"
    },
    {
      image: `/images/courses/pca_-_programa_de_conservacao_auditiva.webp`,
      title: "PCA - Programa de Conservação Auditiva",
      description: "Carga Horária: 1 hora",
      link: "https://ead.salusocupacional.com.br/curso/pca-programa-de-conservacao-auditiva/"
    },
    {
      image: `/images/courses/ppr_-_programa_de_protecao_respiratoria.webp`,
      title: "PPR - Programa de Proteção Respiratória",
      description: "Carga Horária: 1 hora",
      link: "https://ead.salusocupacional.com.br/curso/ppr-programa-de-protecao-respiratoria/"
    },
    {
      image: `/images/courses/primeiros_socorros.webp`,
      title: "Primeiros Socorros",
      description: "Carga Horária: 10 horas",
      link: "https://ead.salusocupacional.com.br/curso/primeiros-socorros/"
    },
    {
      image: `/images/courses/rac_01_-_trabalhos_em_altura.webp`,
      title: "RAC 01 - Trabalhos Em Altura",
      description: "Carga Horária: 2 horas",
      link: "https://ead.salusocupacional.com.br/curso/rac-01-trabalhos-em-altura/"
    },
    {
      image: `/images/courses/rac_02_-_veiculos_automotores_leves.webp`,
      title: "RAC 02 - Veículos Automotores Leves",
      description: "Carga Horária: 2 horas",
      link: "https://ead.salusocupacional.com.br/curso/rac-02-veiculos-automotores-leves/"
    },
    {
      image: `/images/courses/rac_03_-_operacao_de_equipamentos_moveis.webp`,
      title: "RAC 03 - Operação De Equipamentos Móveis",
      description: "Carga Horária: 2 horas",
      link: "https://ead.salusocupacional.com.br/curso/rac-03-operacao-de-equipamentos-moveis/"
    },
    {
      image: `/images/courses/rac_04_-_bloqueio_identificacao_e_zero_energia.webp`,
      title: "RAC 04 - Bloqueio, Identificação E Zero Energia",
      description: "Carga Horária: 2 horas",
      link: "https://ead.salusocupacional.com.br/curso/rac-04-bloqueio-identificacao-e-zero-energia/"
    },
    {
      image: `/images/courses/rac_05_-_icamento_de_carga.webp`,
      title: "RAC 05 - Içamento De Carga",
      description: "Carga Horária: 2 horas",
      link: "https://ead.salusocupacional.com.br/curso/rac-05-icamento-de-carga/"
    },
    {
      image: `/images/courses/rac_06_-_espaco_confinado.webp`,
      title: "RAC 06 - Espaço Confinado",
      description: "Carga Horária: 2 horas",
      link: "https://ead.salusocupacional.com.br/curso/rac-06-espaco-confinado/"
    },
    {
      image: `/images/courses/rac_07_-_protecao_de_maquinas.webp`,
      title: "RAC 07 - Proteção De Máquinas",
      description: "Carga Horária: 2 horas",
      link: "https://ead.salusocupacional.com.br/curso/rac-07-protecao-de-maquinas/"
    },
    {
      image: `/images/courses/rac_08_-_atividades_no_terreno.webp`,
      title: "RAC 08 - Atividades No Terreno",
      description: "Carga Horária: 2 horas",
      link: "https://ead.salusocupacional.com.br/curso/rac-08-atividades-no-terreno/"
    },
    {
      image: `/images/courses/rac_09_-_explosivos.webp`,
      title: "RAC 09 - Explosivos",
      description: "Carga Horária: 2 horas",
      link: "https://ead.salusocupacional.com.br/curso/rac-09-explosivos/"
    },
    {
      image: `/images/courses/rac_10_-_trabalhos_em_eletricidade.webp`,
      title: "RAC 10 - Trabalhos Em Eletricidade",
      description: "Carga Horária: 2 horas",
      link: "https://ead.salusocupacional.com.br/curso/rac-10-trabalhos-em-eletricidade/"
    },
    {
      image: `/images/courses/rac_11_-_metal_liquido.webp`,
      title: "RAC 11 - Metal Líquido",
      description: "Carga Horária: 2 horas",
      link: "https://ead.salusocupacional.com.br/curso/rac-11-metal-liquido/"
    }
  ];

  // Aplicar ordenação aos cursos
  const carouselItems = sortCourses([...carouselItemsUnsorted]);

  // Função para filtrar cursos baseada no termo de pesquisa
  const filteredCourses = carouselItems.filter(course => 
    course.title.toLowerCase().includes(courseSearchFilter.toLowerCase()) ||
    course.description.toLowerCase().includes(courseSearchFilter.toLowerCase())
  );

  // Função para destacar texto encontrado
  const highlightText = (text: string, searchTerm: string) => {
    if (!searchTerm) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? 
        <span key={index} className="bg-yellow-300 text-black px-1 rounded">{part}</span> : 
        part
    );
  };

  const handleMobileMenuToggle = () => {
    if (isMobileMenuOpen) {
      setIsMenuVisible(false);
      setTimeout(() => {
        setIsMobileMenuOpen(false);
      }, 300);
    } else {
      setIsMobileMenuOpen(true);
      if (!isScrolled) {
        setTimeout(() => {
          setIsMenuVisible(true);
        }, 500);
      } else {
        setIsMenuVisible(true); 
      }
    }
  };

  // Function to scroll to top
  const scrollToTop = (event: React.MouseEvent) => {
    event.preventDefault(); // Prevent potential default anchor behavior if wrapped in <a>
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // WhatsApp Link Configuration
  const whatsappNumber = "5561998432613"; // Brazil code + Area code + Number
  const whatsappMessage = encodeURIComponent("Olá, SALUS! Gostaria de tirar algumas dúvidas.");
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  // RE-ADD: Handler to OPEN the modal
  const handleWhatsAppRedirect = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault(); 
    setIsModalOpen(true); // Open the modal
  };

  // RE-ADD: Action to perform on modal confirmation
  const confirmWhatsAppRedirect = () => {
    window.open(whatsappLink, '_blank', 'noopener,noreferrer');
    setIsModalOpen(false); // Close modal after confirmation
    document.body.style.overflow = 'unset'; // Ensure scroll is unlocked
  };

  // RE-ADD: Function to close the modal
  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'unset'; // Ensure scroll is unlocked
  };

  // Functions for Consultation Modal
  const openConsultationModal = (event: React.MouseEvent) => {
    event.preventDefault();
    setIsConsultationModalOpen(true);
  };

  const closeConsultationModal = () => {
    setIsConsultationModalOpen(false);
  };

  // Function to close the WhatsApp bubble (triggers exit animation)
  const handleCloseWhatsAppBubble = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation(); // Prevent click from propagating to the button below
    setBubbleAnimationClass('animate-slide-out-bubble'); // Trigger exit animation
    // Clear any pending hide timeout before setting a new one
    if (hideBubbleTimeoutRef.current) {
      clearTimeout(hideBubbleTimeoutRef.current);
    }
    hideBubbleTimeoutRef.current = setTimeout(() => {
        setShowWhatsAppBubble(false); // Actually hide after animation
    }, 300); // Corresponds to animation duration
  };

  // ADD Function to toggle course dropdown
  const toggleCourseDropdown = () => {
      setIsCourseDropdownOpen(prevState => !prevState);
  };

  // ADD Function to toggle document dropdown
  const toggleDocumentDropdown = () => {
      setIsDocumentDropdownOpen(prevState => !prevState);
  };

  return (
    <div className="min-h-screen bg-salus-branco font-body">
      {/* Navigation */}
      <nav className="fixed w-full z-50">
        {/* Animated background element - Apply dynamic width via style */}
        <div
          onClick={scrollToTop}
          className={`
            absolute top-0 left-0 h-full z-0 
            transition-all duration-500 ease-in-out 
            ${(isScrolled || isMobileMenuOpen) 
              ? 'w-full rounded-none shadow-lg bg-salus-branco' 
              : 'rounded-r-full shadow-none bg-salus-branco/90' /* Removed fixed width w-64 */
            }
          `}
          style={!(isScrolled || isMobileMenuOpen) && backgroundWidth ? { width: `${backgroundWidth}px` } : {}}
        />

        {/* Navbar content - Add relative and z-10 */}
        <div className="container mx-auto px-6 py-4 relative z-10"> 
          <div className="flex items-center justify-between">
            {/* Logo - Add ref and onLoad handler */}
            <img 
              ref={logoRef} 
              src="/logo.webp" 
              alt="SALUS" 
              className="h-16 mr-4 cursor-pointer"
              onClick={scrollToTop}
              onLoad={
                () => {
                  if (logoRef.current) {
                    const logoRightEdge = logoRef.current.getBoundingClientRect().right;
                    const padding = 16; 
                    setBackgroundWidth(logoRightEdge + padding);
                  }
                }
              }
            /> 
            
            {/* Desktop Menu - text color still needs to adapt */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#services" className={`hover:text-salus-verde-escuro transition-colors ${isScrolled ? 'text-salus-azul-escuro' : 'text-salus-branco'}`}>Serviços</a>
              <a href="#why-salus" className={`hover:text-salus-verde-escuro transition-colors ${isScrolled ? 'text-salus-azul-escuro' : 'text-salus-branco'}`}>Importância</a>
              <a href="#contact" className={`hover:text-salus-verde-escuro transition-colors ${isScrolled ? 'text-salus-azul-escuro' : 'text-salus-branco'}`}>Contato</a>
              <a 
                href={whatsappLink} // Keep href for context/SEO
                onClick={handleWhatsAppRedirect} // RE-ADD onClick handler
                target="_blank"
                rel="noopener noreferrer"
                className="bg-salus-verde-escuro text-salus-branco px-6 py-2 rounded-full hover:bg-salus-verde-claro transition-colors"
              >
                Fale Conosco
              </a>
            </div>

            {/* Mobile Menu Button - text color still needs to adapt */}
            <button 
              className={`md:hidden transition-colors ${isScrolled ? 'text-salus-azul-escuro' : 'text-salus-branco'}`}
              onClick={handleMobileMenuToggle}
            >
              {isMobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {/* Mobile Menu - Always render, control visibility with classes */}
        <div 
          className={`
            md:hidden bg-salus-branco shadow-lg relative z-10 
            transition-all duration-300 ease-in-out overflow-hidden 
            ${isMenuVisible ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'} 
          `}
        > 
            <div className="container mx-auto px-6 py-4">
            <div className="flex flex-col space-y-4 items-start">
              <a href="#services" className="text-salus-azul-escuro hover:text-salus-verde-escuro">Serviços</a>
              <a href="#why-salus" className="text-salus-azul-escuro hover:text-salus-verde-escuro">Importância</a>
              <a href="#contact" className="text-salus-azul-escuro hover:text-salus-verde-escuro">Contato</a>
              <a 
                href={whatsappLink} // Keep href for context/SEO
                onClick={handleWhatsAppRedirect} // RE-ADD onClick handler
                target="_blank"
                rel="noopener noreferrer"
                className="bg-salus-verde-escuro text-salus-branco px-6 py-2 rounded-full hover:bg-salus-verde-claro transition-colors w-full text-left"
              >
                  Fale Conosco
              </a>
              </div>
            </div>
          </div>
      </nav>

      {/* Hero Section - Adjust padding for smaller screens */}
      <header className="relative min-h-screen flex items-center pt-20 pb-12 md:pt-24 md:pb-16"> {/* Adjusted vertical padding */}
        <div className="absolute inset-0 z-0">
          {/* Main color overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-salus-azul-escuro/90 to-salus-verde-escuro/80 z-10" /> 
          
          <img 
            src="/fundo-principal.webp"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="container mx-auto px-6 relative z-20">
          <div className="text-salus-branco">
            <HeroCarousel />
            
            {/* Keep the CTA button below the carousel area - Adjust margin */}
            <div className="mt-16 md:mt-20"> {/* Adjusted top margin */}
              <button 
                onClick={openConsultationModal} // Open form modal
                className="bg-salus-verde-claro text-salus-branco px-8 py-4 rounded-full text-lg font-semibold hover:bg-salus-verde-escuro transition-colors flex items-center gap-2"
              >
                Solicite uma Consultoria
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* NEW Services Carousel Section */}
      <section 
        ref={carouselSectionRef}
        className={`py-20 bg-salus-azul-escuro transition-all duration-700 ease-out ${isCarouselVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      >
        <ServicesCarousel items={carouselItems} />

        {/* --- Course Dropdown Section --- */}
        <div className="container mx-auto px-6 mt-12 text-center">
            <button 
                onClick={toggleCourseDropdown}
                className="bg-salus-verde-escuro text-salus-branco px-6 py-3 rounded-lg hover:bg-salus-verde-claro transition-colors inline-flex items-center gap-2"
                aria-expanded={isCourseDropdownOpen}
                aria-controls="course-list-dropdown"
            >
                {isCourseDropdownOpen ? 'Ocultar Lista de Cursos' : 'Ver Todos os Cursos'}
                {/* Optional: Add an icon like ChevronDown/Up */}
                 <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`w-5 h-5 transition-transform duration-300 ${isCourseDropdownOpen ? 'rotate-180' : ''}`}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                 </svg>
            </button>
              </div>
        
        {/* Dropdown Content */}
        <div 
            id="course-list-dropdown"
            className={`overflow-hidden transition-all duration-500 ease-in-out ${isCourseDropdownOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}
        >
            <div className="container mx-auto px-6 pt-10 pb-16">
                {/* Barra de Pesquisa */}
                <div className="mb-8">
                    <div className="relative max-w-md mx-auto">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-5 w-5 text-salus-branco/60" />
                        </div>
                        <input
                            type="text"
                            placeholder="Pesquisar cursos..."
                            value={courseSearchFilter}
                            onChange={(e) => setCourseSearchFilter(e.target.value)}
                            className="block w-full pl-10 pr-3 py-3 border border-white/20 rounded-lg bg-white/10 text-salus-branco placeholder-salus-branco/60 focus:outline-none focus:ring-2 focus:ring-salus-verde-claro focus:border-transparent backdrop-blur-sm transition-all duration-200"
                        />
                    </div>
                    {courseSearchFilter && (
                        <div className="text-center mt-3">
                            <span className="text-salus-branco/80 text-sm">
                                {filteredCourses.length} curso{filteredCourses.length !== 1 ? 's' : ''} encontrado{filteredCourses.length !== 1 ? 's' : ''}
                            </span>
                            <button
                                onClick={() => setCourseSearchFilter('')}
                                className="ml-3 text-salus-verde-claro hover:text-salus-branco text-sm underline transition-colors"
                            >
                                Limpar filtro
                            </button>
                        </div>
                    )}
                </div>
                <div className="max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-salus-verde-claro/50 hover:scrollbar-thumb-salus-verde-claro/80 pr-2">
                    <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-10 gap-y-4 text-left pb-6">
                        {filteredCourses.length > 0 ? (
                            filteredCourses.map((item, index) => (
                                <li key={index}>
                                    <a 
                                       href={item.link}
                                       target="_blank"
                                       rel="noopener noreferrer"
                                       className="text-salus-branco/90 hover:text-salus-branco text-base rounded-md transition-colors duration-150 py-3 px-3 shadow bg-white/10 hover:bg-white/20 flex items-center gap-2 min-h-[60px] h-full block cursor-pointer"
                                    >
                                        <GraduationCap className="w-4 h-4 text-salus-verde-claro flex-shrink-0" />
                                        <div className="flex flex-col">
                                            <span className="leading-tight font-medium">
                                                {highlightText(item.title, courseSearchFilter)}
                                            </span>
                                            <span className="text-xs text-salus-branco/70 mt-1">
                                                {highlightText(item.description, courseSearchFilter)}
                                            </span>
                                        </div>
                                    </a>
                                </li>
                            ))
                        ) : (
                            <li className="col-span-full text-center py-8">
                                <div className="text-salus-branco/60">
                                    <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                    <p className="text-lg mb-2">Nenhum curso encontrado</p>
                                    <p className="text-sm">Tente usar outros termos de pesquisa</p>
                                </div>
                            </li>
                        )}
                    </ul>
                </div>
          </div>
        </div>
        {/* --- End Course Dropdown Section --- */}

      </section>

      {/* Services Section */}
      <section 
        id="services" 
        ref={servicesSectionRef} 
        className={`pt-32 pb-8 bg-salus-branco-secundario overflow-hidden`}
      >
        <div className="container mx-auto px-6">
          <div 
            className={`max-w-3xl mx-auto text-center mb-20 transition-all duration-700 ease-out ${isServicesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            <h2 className="text-4xl font-bold mb-6 text-salus-azul-escuro font-heading">Soluções Completas em Saúde e Segurança do Trabalho</h2>
            <p className="text-xl text-salus-azul-escuro opacity-90">
              Oferecemos um conjunto completo de serviços para garantir a segurança e o bem-estar dos seus colaboradores.
            </p>
          </div>
          <div 
            ref={servicesGridRef} 
            className={`grid grid-cols-1 md:grid-cols-3 gap-12 items-stretch transition-all duration-700 ease-out delay-200 ${isServicesGridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            {services.map((service, index) => (
              <ServiceCard key={index} service={service} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* --- NEW Documents Section --- */}
      <section 
        ref={documentsSectionRef}
        className={`pt-4 pb-24 bg-salus-branco-secundario transition-all duration-700 ease-out ${isDocumentsSectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      >
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
              {/* Dropdown Toggle Button */}
              <button 
                  onClick={toggleDocumentDropdown}
                  className="bg-salus-verde-escuro text-salus-branco px-6 py-3 rounded-lg hover:bg-salus-verde-claro transition-colors inline-flex items-center gap-2 text-sm"
                  aria-expanded={isDocumentDropdownOpen}
                  aria-controls="document-list-dropdown"
              >
                  {isDocumentDropdownOpen ? 'Ocultar Lista de Documentos' : 'Ver Lista de Documentos'}
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`w-4 h-4 transition-transform duration-300 ${isDocumentDropdownOpen ? 'rotate-180' : ''}`}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                  </svg>
              </button>
          </div>
          {/* Dropdown Content Area */}
          <div 
            id="document-list-dropdown"
            className={`overflow-hidden transition-all duration-500 ease-in-out ${isDocumentDropdownOpen ? 'max-h-[1000px] opacity-100 pt-8' : 'max-h-0 opacity-0'}`}
          >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
                  {offeredDocuments.map((doc, index) => (
                      <div key={index} className="flex items-center gap-3 p-4 rounded-lg hover:bg-salus-branco transition-colors shadow bg-salus-branco min-h-[80px] h-full">
                          <FileText className="w-5 h-5 text-salus-verde-escuro flex-shrink-0" />
                          <span className="text-base text-salus-azul-escuro leading-tight">{doc.title}</span>
                      </div>
                  ))}
              </div>
            </div>
          {/* End Dropdown Content Area */}
        </div>
      </section>
      {/* --- End NEW Documents Section --- */}

      {/* Why Salus Section - Add Ref and conditional classes */}
      <section 
        id="why-salus" 
        ref={whySalusSectionRef}
        className={`py-32 bg-salus-branco transition-all duration-700 ease-out ${isWhySalusVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      >
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            {/* Left Column: Text and Benefits */}
            <div>
              <h2 className="text-4xl font-bold mb-4 text-salus-azul-escuro font-heading">
                Por que a Saúde e Segurança do Trabalho é essencial?
              </h2>
              <div className="w-24 h-1 bg-salus-verde-claro mb-8"></div>
              <p className="text-salus-azul-escuro opacity-90 mb-12">
                Investir em Saúde e Segurança do Trabalho não é apenas uma obrigação legal, mas uma estratégia inteligente para reduzir custos, aumentar a produtividade e melhorar a imagem da sua empresa. Conheça os principais benefícios:
              </p>
              <div className="space-y-8">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="flex-shrink-0 bg-salus-verde-claro/20 p-3 rounded-lg">
                      <benefit.icon className="w-6 h-6 text-salus-verde-escuro" />
          </div>
                  <div>
                      <h3 className="text-lg font-semibold mb-1 text-salus-azul-escuro font-heading">{benefit.title}</h3>
                      <p className="text-salus-azul-escuro opacity-80 text-sm">{benefit.description}</p>
                    </div>
                  </div>
                ))}
                  </div>
                </div>

            {/* Right Column: Quote Block */}
            <div className="flex justify-center">
              {/* Make card relative for background image positioning */}
              <div className="bg-salus-branco-secundario rounded-2xl shadow-xl p-8 lg:p-12 max-w-lg relative overflow-hidden"> 
                {/* Background Logo Image */}
                <img 
                  src="/SALUS trevo logo.webp" 
                  alt="" 
                  className="absolute bottom-0 right-0 w-48 h-48 opacity-10 -mr-8 -mb-8 z-0" 
                />

                {/* Content - Ensure it's above background image */}
                <div className="relative z-10">
                  <div className="w-16 h-0.5 bg-salus-verde-claro mb-3"></div>
                  {/* Changed SALUS paragraph to h3 title */}
                  <h3 className="text-2xl font-bold font-heading text-salus-verde-escuro mb-6 tracking-wider">SALUS</h3>
                  <blockquote className="text-2xl font-semibold text-salus-azul-escuro leading-relaxed">
                    "Nossa missão é transformar a cultura de segurança nas empresas, protegendo vidas e promovendo ambientes de trabalho saudáveis."
                  </blockquote>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Add Ref and conditional classes */}
      <section 
        ref={ctaSectionRef}
        className={`py-32 bg-gradient-to-r from-salus-azul-escuro to-salus-verde-escuro text-salus-branco transition-all duration-700 ease-out ${isCtaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      >
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6 font-heading">Pronto para Transformar a Segurança da sua Empresa?</h2>
            <p className="text-xl mb-12 text-salus-branco-secundario opacity-90">
              Entre em contato hoje mesmo e descubra como podemos ajudar sua empresa a alcançar excelência em saúde e segurança do trabalho.
            </p>
            <button 
              onClick={openConsultationModal} // ADD onClick handler
              className="bg-salus-branco text-salus-verde-escuro px-8 py-4 rounded-full text-lg font-semibold hover:bg-salus-branco-secundario transition-colors"
            >
              Agende uma Consultoria Gratuita
            </button>
          </div>
        </div>
      </section>

      {/* Contact Section - Update Phone Number */}
      <section 
        id="contact" 
        ref={contactSectionRef} 
        className={`py-32 bg-salus-branco-secundario overflow-hidden`} 
      >
        <div className="container mx-auto px-6">
          {/* Apply animation to the intro text block */}
          <div 
            className={`text-center mb-20 transition-all duration-700 ease-out ${isContactVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            <h2 className="text-4xl font-bold mb-6 text-salus-azul-escuro font-heading">Entre em Contato</h2>
            <p className="text-xl text-salus-azul-escuro opacity-90">
                Estamos prontos para atender sua empresa e desenvolver soluções personalizadas.
              </p>
            </div>
          {/* Apply animation to the grid container */}
          <div 
            ref={contactGridRef} 
            className={`grid grid-cols-1 md:grid-cols-3 gap-12 text-center transition-all duration-700 ease-out delay-200 ${isContactGridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            {/* Phone Card - Updated Number */}
            <div className="bg-salus-branco p-8 rounded-2xl shadow-lg text-center">
              <div className="flex justify-center items-center gap-3 mb-6">
                <Phone className="w-10 h-10 text-salus-verde-escuro" />
                {/* Keep WhatsApp icon here as well? Or just phone? Keeping for now */}
                <MessageSquare className="w-10 h-10 text-salus-verde-escuro" /> 
              </div>
              <p className="text-lg font-semibold mb-2 text-salus-azul-escuro">Telefone / WhatsApp</p>
              <p className="text-salus-azul-escuro opacity-90">(61) 99843-2613</p> {/* Updated Number */}
              </div>
            {/* Email Card - Using Flexbox for content centering */}
            <div className="bg-salus-branco p-8 rounded-2xl shadow-lg flex flex-col items-center text-center"> {/* Added flex flex-col items-center */}
              <Mail className="w-12 h-12 text-salus-verde-escuro mb-6" /> {/* Removed mx-auto as items-center handles it */}
              <p className="text-lg font-semibold mb-2 text-salus-azul-escuro">E-mail</p>
              {/* Removed w-full and text-center from here, relies on parent items-center */}
              <p className="text-salus-azul-escuro opacity-90 text-sm md:text-base">
                contato@salusocupacional.com.br
              </p> 
              </div>
            {/* Address Card - Applying similar flex centering for consistency */}
            <div className="bg-salus-branco p-8 rounded-2xl shadow-lg flex flex-col items-center text-center"> {/* Added flex flex-col items-center */}
              <MapPin className="w-12 h-12 text-salus-verde-escuro mb-6" /> {/* Removed mx-auto */}
              <p className="text-lg font-semibold mb-2 text-salus-azul-escuro">Endereço</p>
              <p className="text-salus-azul-escuro opacity-90 text-sm md:text-base">
                Brasília - DF
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Removed Ref and animation classes */}
      <footer 
        className={`bg-salus-azul-escuro text-salus-branco pb-16`} /* Removed animation classes */
      >
        <div className="container mx-auto px-6">
          {/* Modern Logo Tag Design */} 
          <div className="relative max-w-sm mx-auto"> 
            {/* Modern Tag Shape with Gradient and Shadow */}
            <div className="relative">
              {/* Main tag body */}
              <div className="bg-gradient-to-br from-salus-branco via-salus-branco-secundario to-gray-100 rounded-2xl shadow-2xl p-8 pt-12 pb-6 relative overflow-hidden">
                {/* Decorative corner accent */}
                <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-salus-verde-claro/20 to-transparent rounded-bl-3xl"></div>
                
                {/* Logo */}
                <div className="text-center mb-6">
                  <img 
                    src="/SALUS vertical logo.webp" 
                    alt="SALUS" 
                    className="h-20 w-auto mx-auto drop-shadow-sm"
                  /> 
                </div>
                
                {/* Tagline with modern typography */}
                <p className="text-salus-azul-escuro/80 text-center text-sm font-medium leading-relaxed"> 
                  Excelência em segurança do trabalho para sua empresa.
                </p>
                
                {/* Bottom accent line */}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-16 h-1 bg-gradient-to-r from-salus-verde-claro to-salus-verde-escuro rounded-t-full"></div>
              </div>
              
              {/* Tag hole effect */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-6 h-6 bg-salus-azul-escuro rounded-full border-4 border-salus-branco shadow-lg"></div>
            </div>
          </div>

          {/* REMOVE Service Links Column */}

          {/* REMOVE Company Links Column */}

          {/* REMOVE Legal Links Column */}
          
          {/* Keep Copyright section */} 
          <div className="border-t border-gray-700 mt-12 pt-8 text-center text-gray-400">
            <p>© 2025 SALUS. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>

      {/* --- WhatsApp Floating Button and Bubble Container --- */}
      <div className="fixed bottom-6 right-6 z-50 flex items-end">
        {/* WhatsApp Bubble (conditionally rendered) */}
        {showWhatsAppBubble && (
          <div 
            onClick={handleCloseWhatsAppBubble} 
            onTouchStart={handleCloseWhatsAppBubble} 
            className={`relative bg-salus-branco text-salus-azul-escuro px-4 py-2 rounded-lg shadow-md mr-3 mb-2 cursor-pointer ${bubbleAnimationClass}`} // Apply dynamic animation class
          >
            {/* Visual Close Icon */}
            <X size={14} className="absolute top-1 right-1 text-gray-400" /> 
            <p className="text-sm pr-3">👷‍♂️ Entre em contato com a gente pelo WhatsApp!</p> {/* Add padding-right for X */}
          </div>
        )}

        {/* Simplified Floating WhatsApp Button */}
        <a
          ref={whatsAppButtonRef} 
          href={whatsappLink} 
          onClick={handleWhatsAppRedirect} 
          onMouseEnter={() => {
            // Clear any pending hide timeout
            if (hideBubbleTimeoutRef.current) {
                clearTimeout(hideBubbleTimeoutRef.current);
                hideBubbleTimeoutRef.current = null; // Clear the ref
            }
            // Always show bubble on hover and set entry animation
            setShowWhatsAppBubble(true);
            setBubbleAnimationClass('animate-slide-in-bubble'); 
          }} 
          onMouseLeave={() => {
             // Trigger exit animation on mouse leave
             setBubbleAnimationClass('animate-slide-out-bubble');
             // Clear any previous hide timeout before setting new one
             if (hideBubbleTimeoutRef.current) {
                 clearTimeout(hideBubbleTimeoutRef.current);
             }
             hideBubbleTimeoutRef.current = setTimeout(() => {
                 setShowWhatsAppBubble(false);
             }, 300);
          }}
          aria-label="Entre em contato pelo WhatsApp" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="bg-salus-verde-claro text-salus-azul-escuro p-4 rounded-full shadow-lg hover:bg-salus-verde-escuro hover:text-white transition-colors flex items-center justify-center"
        >
          <MessageSquare size={28} />
        </a>
      </div>

      {/* Render the Modals */}
      <ConfirmationModal // RE-ADD the Confirmation Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        onConfirm={confirmWhatsAppRedirect}
        message="Você será redirecionado para o WhatsApp. Deseja continuar?"
        confirmText="Continuar"
        cancelText="Cancelar"
      />
      <ConsultationFormModal
        isOpen={isConsultationModalOpen}
        onClose={closeConsultationModal}
      />

    </div>
  );
}

export default App;